import React, { useEffect, useState } from "react";
import {
  Switch,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
  FormHelperText,
  Tooltip,
  IconButton,
  FormControl,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import Colors from "../../theme/colors.json";
import InfoIcon from "@mui/icons-material/Info";
import { fetchRuntimeFormData } from "../../services/FormService";
import { DECISION_ENUMS } from "../../constants/constants";

const useStyles = makeStyles((theme) => ({
  notificationSettingsSection: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  notificationSettingsHeadingSection: {
    display: "flex",
    justifyContent: "flex-start",
    width: "95%",
    marginBottom: "0.5%",
    borderRadius: "0.5vw",
  },
  notificationSettingsHeading: {
    color: theme.palette.secondary.main,
    fontSize: theme.fontSizes.MEDIUM,
    fontWeight: 600,
  },
  notificationSettingsFormContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "1.5vw",
    background: Colors.WHITE_BACKGROUND,
    padding: "2.5% 2.5% 2.5% 2.5%",
    borderRadius: "0.6rem",
    fontSize: theme.fontSizes.SMALL,
    width: "95%",
  },
  formControl: {
    margin: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: "25px",
    width: "95%",
    justifyContent: "space-between",
    alignItems: "center",
  },
  switchReminder: {
    width: "30%",
    margin: "0",
  },
  recieveEmails: {
    width: "30%",
  },
  emailFrequency: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
  },
  checkbox: {
    margin: "0",
  },
  formGroupContainer: {
    width: "30%",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
  },
  infoIcon: {
    marginLeft: theme.spacing(1),
  },
  formSection: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  },
}));

const ReminderSettings = ({ reminderDetails, emails, type }) => {
  const classes = useStyles();

  return (
    <div className={classes.formSection}>
      <FormControlLabel
        control={
          <Switch
            checked={reminderDetails.values[`${type}Reminder`]}
            onChange={(event) => {
              reminderDetails.setFieldValue(
                `${type}Reminder`,
                event.target.checked
              );
              console.log(`${type}Reminder: `, event.target.checked); // Debugging log
            }}
            name={`${type}Reminder`}
            color="primary"
          />
        }
        className={classes.switchReminder}
        label={`${type.charAt(0).toUpperCase() + type.slice(1)} Reminder`}
      />
      {reminderDetails.values[`${type}Reminder`] && (
        <>
          <div className={classes.formGroupContainer}>
            <div>Choose how often you’d like to receive email reminders:</div>
            <FormGroup className={classes.emailFrequency}>
              <FormControlLabel
                control={
                  <Checkbox
                    name={`${type}ReminderFrequency`}
                    value="45"
                    checked={reminderDetails?.values[
                      `${type}ReminderFrequency`
                    ]?.includes("45")}
                    onChange={reminderDetails?.handleChange}
                    className={classes.checkbox}
                  />
                }
                label="45 days"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name={`${type}ReminderFrequency`}
                    value="30"
                    checked={reminderDetails?.values[
                      `${type}ReminderFrequency`
                    ]?.includes("30")}
                    onChange={reminderDetails?.handleChange}
                    className={classes.checkbox}
                  />
                }
                label="30 days"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name={`${type}ReminderFrequency`}
                    value="15"
                    checked={reminderDetails?.values[
                      `${type}ReminderFrequency`
                    ]?.includes("15")}
                    onChange={reminderDetails?.handleChange}
                    className={classes.checkbox}
                  />
                }
                label="15 days"
              />
            </FormGroup>
            {reminderDetails.touched[`${type}ReminderFrequency`] &&
              reminderDetails.errors[`${type}ReminderFrequency`] && (
                <FormHelperText error sx={{ textAlign: "center" }}>
                  {reminderDetails.errors[`${type}ReminderFrequency`]}
                </FormHelperText>
              )}
          </div>
          <FormControl className={classes.recieveEmails}>
            <Autocomplete
              multiple
              disableClearable
              fullWidth
              id={`${type}ReminderEmails`}
              options={emails}
              getOptionLabel={(option) => option}
              value={reminderDetails?.values[`${type}ReminderEmails`]}
              onChange={(_, newValue) => {
                reminderDetails?.setFieldValue(
                  `${type}ReminderEmails`,
                  newValue
                );
              }}
              onBlur={reminderDetails?.handleBlur}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Emails"
                  size="small"
                  error={
                    reminderDetails.touched[`${type}ReminderEmails`] &&
                    Boolean(reminderDetails.errors[`${type}ReminderEmails`])
                  }
                  helperText={
                    reminderDetails.touched[`${type}ReminderEmails`] &&
                    reminderDetails.errors[`${type}ReminderEmails`]
                  }
                />
              )}
              renderOption={(props, option) => (
                <MenuItem {...props} value={option}>
                  {option}
                </MenuItem>
              )}
            />
          </FormControl>
        </>
      )}
    </div>
  );
};

const EmailReminders = ({
  emailReminderDetails,
  proposalDataRedux,
  isPolicyPlacementDoneWithTrovity,
  isPolicyPremiumIsPaidInInstallments,
}) => {
  const classes = useStyles();
  const [emails, setEmails] = useState<any>([]);

  const getClientEmails = async () => {
    const clientEmailDataPromise = fetchRuntimeFormData(
      "1204713369670594560",
      0,
      100000
    );
    const internalEmployeeEmailDataPromise = fetchRuntimeFormData(
      "1205444077359804416",
      0,
      100000
    );

    const [clientEmailData, internalEmployeeEmailData] = await Promise.all([
      clientEmailDataPromise,
      internalEmployeeEmailDataPromise,
    ]);

    const clientEmail = proposalDataRedux?.formData?.clientEmail;

    const clientEmailRecords = clientEmailData?.data?.content?.filter(
      (record) => record.formData.clientEmailId === clientEmail
    );

    const clientEmployeeEmails =
      clientEmailRecords[0]?.formData?.employeesList
        ?.map((employee) => employee?.email)
        .filter((email) => email !== "") || [];

    const internalEmployeeEmails =
      internalEmployeeEmailData?.data?.content
        ?.map((record) => record?.formData?.employeeEMailId)
        .filter((email) => email !== "") || [];

    const finalEmailList = [
      clientEmail,
      ...clientEmployeeEmails,
      ...internalEmployeeEmails,
    ];

    await setEmails(finalEmailList);
  };

  useEffect(() => {
    getClientEmails();
  }, []);

  return (
    <div className={classes.notificationSettingsSection}>
      <div className={classes.notificationSettingsHeadingSection}>
        <div className={classes.notificationSettingsHeading}>
          Customize Your Email Reminder Preferences
        </div>
      </div>
      <div className={classes.notificationSettingsFormContainer}>
        <form
          onSubmit={emailReminderDetails.handleSubmit}
          className={classes.formControl}
        >
          <ReminderSettings
            reminderDetails={emailReminderDetails}
            emails={emails}
            type="renewal"
          />
          {isPolicyPremiumIsPaidInInstallments === DECISION_ENUMS.YES &&
            isPolicyPlacementDoneWithTrovity !== DECISION_ENUMS.NO && (
              <ReminderSettings
                reminderDetails={emailReminderDetails}
                emails={emails}
                type="installment"
              />
            )}
        </form>
      </div>
    </div>
  );
};

export default EmailReminders;
